/**
 * FHIR API facade for Patient and related resource types.
 *
 * Delegates all operations to FhirApiService. Provides typed get/put/patch/create/search/delete for Patient,
 * Practitioner, Organization, RelatedPerson, Encounter, Location.
 */

import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import type {
  FHIRAppointment,
  FHIREncounter,
  FHIRLocation,
  FHIROrganization,
  FHIRPatient,
  FHIRPractitioner,
  FHIRRelatedPerson,
} from '../models/fhir.model';
import { FhirApiService } from './fhir-api.service';
import type {
  AppointmentSearchParams,
  EncounterSearchParams,
  FhirBundle,
  LocationSearchParams,
  OrganizationSearchParams,
  PatientSearchParams,
  PractitionerSearchParams,
  RelatedPersonSearchParams,
} from './fhir-api.types';

/** Resource type names supported by FhirResourceApiService. */
export type FhirResourceApiType =
  | 'Patient'
  | 'Practitioner'
  | 'Organization'
  | 'RelatedPerson'
  | 'Encounter'
  | 'Location';

@Injectable()
export class FhirResourceApiService {
  private readonly fhirApi = inject(FhirApiService);

  // ---------- Patient ----------

  getPatient(id: string): Observable<FHIRPatient> {
    return this.fhirApi.get<FHIRPatient>('Patient', id);
  }

  putPatient(patient: FHIRPatient): Observable<FHIRPatient> {
    return this.fhirApi.put(patient);
  }

  patchPatient(id: string, partial: Partial<FHIRPatient>): Observable<FHIRPatient> {
    return this.fhirApi.patch<FHIRPatient>('Patient', id, partial);
  }

  createPatient(patient: FHIRPatient): Observable<FHIRPatient> {
    return this.fhirApi.create(patient);
  }

  searchPatients(params?: PatientSearchParams): Observable<FhirBundle<FHIRPatient>> {
    return this.fhirApi.search<FHIRPatient>('Patient', params);
  }

  deletePatient(id: string): Observable<void> {
    return this.fhirApi.delete('Patient', id);
  }

  // ---------- Practitioner ----------

  getPractitioner(id: string): Observable<FHIRPractitioner> {
    return this.fhirApi.get<FHIRPractitioner>('Practitioner', id);
  }

  putPractitioner(practitioner: FHIRPractitioner): Observable<FHIRPractitioner> {
    return this.fhirApi.put(practitioner);
  }

  patchPractitioner(id: string, partial: Partial<FHIRPractitioner>): Observable<FHIRPractitioner> {
    return this.fhirApi.patch<FHIRPractitioner>('Practitioner', id, partial);
  }

  createPractitioner(practitioner: FHIRPractitioner): Observable<FHIRPractitioner> {
    return this.fhirApi.create(practitioner);
  }

  searchPractitioners(params?: PractitionerSearchParams): Observable<FhirBundle<FHIRPractitioner>> {
    return this.fhirApi.search<FHIRPractitioner>('Practitioner', params);
  }

  deletePractitioner(id: string): Observable<void> {
    return this.fhirApi.delete('Practitioner', id);
  }

  // ---------- Organization ----------

  getOrganization(id: string): Observable<FHIROrganization> {
    return this.fhirApi.get<FHIROrganization>('Organization', id);
  }

  putOrganization(organization: FHIROrganization): Observable<FHIROrganization> {
    return this.fhirApi.put(organization);
  }

  patchOrganization(id: string, partial: Partial<FHIROrganization>): Observable<FHIROrganization> {
    return this.fhirApi.patch<FHIROrganization>('Organization', id, partial);
  }

  createOrganization(organization: FHIROrganization): Observable<FHIROrganization> {
    return this.fhirApi.create(organization);
  }

  searchOrganizations(params?: OrganizationSearchParams): Observable<FhirBundle<FHIROrganization>> {
    return this.fhirApi.search<FHIROrganization>('Organization', params);
  }

  deleteOrganization(id: string): Observable<void> {
    return this.fhirApi.delete('Organization', id);
  }

  // ---------- RelatedPerson ----------

  getRelatedPerson(id: string): Observable<FHIRRelatedPerson> {
    return this.fhirApi.get<FHIRRelatedPerson>('RelatedPerson', id);
  }

  putRelatedPerson(relatedPerson: FHIRRelatedPerson): Observable<FHIRRelatedPerson> {
    return this.fhirApi.put(relatedPerson);
  }

  patchRelatedPerson(id: string, partial: Partial<FHIRRelatedPerson>): Observable<FHIRRelatedPerson> {
    return this.fhirApi.patch<FHIRRelatedPerson>('RelatedPerson', id, partial);
  }

  createRelatedPerson(relatedPerson: FHIRRelatedPerson): Observable<FHIRRelatedPerson> {
    return this.fhirApi.create(relatedPerson);
  }

  searchRelatedPersons(params?: RelatedPersonSearchParams): Observable<FhirBundle<FHIRRelatedPerson>> {
    return this.fhirApi.search<FHIRRelatedPerson>('RelatedPerson', params);
  }

  deleteRelatedPerson(id: string): Observable<void> {
    return this.fhirApi.delete('RelatedPerson', id);
  }

  // ---------- Encounter ----------

  getEncounter(id: string): Observable<FHIREncounter> {
    return this.fhirApi.get<FHIREncounter>('Encounter', id);
  }

  putEncounter(encounter: FHIREncounter): Observable<FHIREncounter> {
    return this.fhirApi.put(encounter);
  }

  patchEncounter(id: string, partial: Partial<FHIREncounter>): Observable<FHIREncounter> {
    return this.fhirApi.patch<FHIREncounter>('Encounter', id, partial);
  }

  createEncounter(encounter: FHIREncounter): Observable<FHIREncounter> {
    return this.fhirApi.create(encounter);
  }

  searchEncounters(params?: EncounterSearchParams): Observable<FhirBundle<FHIREncounter>> {
    return this.fhirApi.search<FHIREncounter>('Encounter', params);
  }

  deleteEncounter(id: string): Observable<void> {
    return this.fhirApi.delete('Encounter', id);
  }

  // ---------- Appointment ----------

  getAppointment(id: string): Observable<FHIRAppointment> {
    return this.fhirApi.get<FHIRAppointment>('Appointment', id);
  }

  putAppointment(appointment: FHIRAppointment): Observable<FHIRAppointment> {
    return this.fhirApi.put(appointment);
  }

  createAppointment(appointment: FHIRAppointment): Observable<FHIRAppointment> {
    return this.fhirApi.create(appointment);
  }

  searchAppointments(params?: AppointmentSearchParams): Observable<FhirBundle<FHIRAppointment>> {
    return this.fhirApi.search<FHIRAppointment>('Appointment', params);
  }

  deleteAppointment(id: string): Observable<void> {
    return this.fhirApi.delete('Appointment', id);
  }

  // ---------- Location ----------

  getLocation(id: string): Observable<FHIRLocation> {
    return this.fhirApi.get<FHIRLocation>('Location', id);
  }

  putLocation(location: FHIRLocation): Observable<FHIRLocation> {
    return this.fhirApi.put(location);
  }

  patchLocation(id: string, partial: Partial<FHIRLocation>): Observable<FHIRLocation> {
    return this.fhirApi.patch<FHIRLocation>('Location', id, partial);
  }

  createLocation(location: FHIRLocation): Observable<FHIRLocation> {
    return this.fhirApi.create(location);
  }

  searchLocations(params?: LocationSearchParams): Observable<FhirBundle<FHIRLocation>> {
    return this.fhirApi.search<FHIRLocation>('Location', params);
  }

  deleteLocation(id: string): Observable<void> {
    return this.fhirApi.delete('Location', id);
  }
}
